import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { useReadUrl } from '../../../hooks/useReadUrl';

interface BookmarkTitleProps {
  bookmarkId: string;
  title: string;
  url: string;
  isLoggedIn: boolean;
  condensedView: boolean;
  profileMode: boolean;
  isBatchMode?: boolean;
  /** When true, modifier-click should select (a different article is open in the reader). */
  hasActiveReaderArticle?: boolean;
}

function BookmarkTitle({
  bookmarkId,
  title,
  url,
  isLoggedIn,
  condensedView,
  profileMode,
  isBatchMode,
  hasActiveReaderArticle = false,
}: BookmarkTitleProps) {
  const routerState = useRouterState();
  const { to, params, search } = useReadUrl(bookmarkId);
  const displayTitle = title || url;
  const isUrl = !title || title.startsWith('http://') || title.startsWith('https://');
  const titleClassName = clsx(
    'no-underline text-bookmark-title',
    'text-copy-primary line-clamp-3 md:line-clamp-2',
    isUrl ? 'break-all' : 'break-words',
  );

  const handleLinkClick = (event: React.MouseEvent) => {
    // Batch mode always selects. Outside batch mode, Shift/Cmd/Ctrl+click only
    // selects when a *different* article is open (hasActiveReaderArticle).
    // Otherwise leave the browser default (open in new tab/window).
    const isSelectionClick =
      isBatchMode ||
      ((event.metaKey || event.ctrlKey || event.shiftKey) && hasActiveReaderArticle);

    if (isSelectionClick) {
      event.preventDefault();
    }
  };

  // Selection-modifier clicks (and any click in batch mode) are row
  // selection gestures. preventDefault on mousedown stops the link from
  // taking focus — browsers otherwise mark Shift+click as :focus-visible
  // and draw an outline on the title.
  const handleLinkMouseDown = (event: React.MouseEvent) => {
    if (isBatchMode || event.shiftKey || event.metaKey || event.ctrlKey) {
      event.preventDefault();
    }
  };

  return (
    <h2
      className={clsx(
        'bookmark-title',
        condensedView && 'condensed',
        profileMode && 'profile-title',
      )}
    >
      {isLoggedIn && !profileMode ? (
        <Link
          to={to}
          params={params}
          search={search}
          className={titleClassName}
          state={{ from: routerState.location.pathname }}
          tabIndex={isBatchMode ? -1 : undefined}
          onClick={handleLinkClick}
          onMouseDown={handleLinkMouseDown}
        >
          {displayTitle}
        </Link>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={titleClassName}
          tabIndex={isBatchMode ? -1 : undefined}
          onMouseDown={handleLinkMouseDown}
        >
          {displayTitle}
        </a>
      )}
    </h2>
  );
}

export default BookmarkTitle;
